import { useAuth } from '@/auth/useAuth'

/**
 * Whether asset QA is shown to this user at all (ORB-982).
 *
 * QA is an internal aid, like Webster. A customer never sees quality checks anywhere in the
 * platform, for the same reason they never see Webster: it is deliberately hypersensitive so our
 * own team can check everything before it reaches them, and "Do not submit yet" is not a sentence
 * the client whose asset it is should ever read.
 *
 * ⚑ This is the same gate Webster uses — `app.tsx` mounts `AIChatWidget` on `isParentCompany` —
 * and it is **not** domain-based, which the call that asked for this assumed it was.
 * `UserProfileService` derives it as `string.IsNullOrEmpty(homeCompanyEntity.ManagedByCompanyId)`:
 * a company is a parent when no other company manages it. OrbitalX is the only one today, so the
 * effect matches "only OrbitalX sees it" while the mechanism is a data relationship.
 *
 * That distinction is load bearing. The roadmap promotes customers to this access level over the
 * next 6-24 months; under `isParentCompany` that promotion is a data change, where an
 * `orbitalx.com` allowlist would be a code change and a deploy, per customer, forever.
 *
 * ⚑⚑ **Every QA surface calls this and nothing reads `isParentCompany` directly.** When QA moves
 * to a permission of its own — which the roadmap implies — this file changes and the call sites
 * do not.
 *
 * ⚑ Loading order is in our favour and worth not breaking: `isParentCompany` is `false` while the
 * profile loads, so QA is hidden first and appears after. It cannot flash a verdict at a customer.
 *
 * ⚑ This gates what the dashboard *shows*. The API still serves QA to any workspace viewer, the
 * same as Webster's own endpoints do — a deliberate, recorded deviation, closed for both by
 * ORB-992.
 */
export function useAssetQaVisible(): boolean {
  const { isParentCompany } = useAuth()
  return isParentCompany
}
